/**
 * InventoryTab - Inventory management for menu items
 * Track stock levels, low stock alerts, and restock items
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Package, AlertTriangle, TrendingDown, TrendingUp, 
  Search, Filter, RefreshCw, Plus, Minus, History,
  CheckCircle, XCircle, Edit2, Save, X, Box
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '../../../utils/apiClient';

const STOCK_REASONS = [
  { value: 'restock', label: 'Restock / Delivery' },
  { value: 'manual_adjustment', label: 'Manual Adjustment' },
  { value: 'waste', label: 'Waste / Spoilage' },
  { value: 'return', label: 'Customer Return' },
  { value: 'stock_count', label: 'Stock Count' },
  { value: 'correction', label: 'Correction' },
];

export default function InventoryTab() {
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [categories, setCategories] = useState([]);
  
  // Modal states
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({
    quantity: 0,
    reason: 'restock',
    notes: ''
  });
  const [history, setHistory] = useState([]);
  const [settingsData, setSettingsData] = useState({});

  // Fetch inventory data
  const fetchInventory = useCallback(async () => {
    try {
      const [invRes, statsRes, catRes] = await Promise.all([
        api.get('/admin/inventory'),
        api.get('/admin/inventory/stats'),
        api.get('/menu/categories')
      ]);
      
      setInventory(invRes.data);
      setStats(statsRes.data);
      setCategories(catRes.data);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
      toast.error('Failed to load inventory');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Filter inventory
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.item_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    
    let matchesStatus = true;
    if (filterStatus === 'low') {
      matchesStatus = item.current_stock <= item.low_stock_threshold && item.current_stock > 0;
    } else if (filterStatus === 'out') {
      matchesStatus = item.current_stock <= 0;
    } else if (filterStatus === 'healthy') {
      matchesStatus = item.current_stock > item.low_stock_threshold;
    }
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Open adjust modal
  const openAdjustModal = (item, isRestock = true) => {
    setSelectedItem(item);
    setAdjustmentData({
      quantity: isRestock ? 10 : -1,
      reason: isRestock ? 'restock' : 'waste',
      notes: ''
    });
    setShowAdjustModal(true);
  };

  // Submit adjustment
  const submitAdjustment = async () => {
    if (!selectedItem) return;
    
    try {
      await api.post(`/admin/inventory/${selectedItem.item_id}/adjust`, adjustmentData);
      toast.success('Stock updated!');
      setShowAdjustModal(false);
      fetchInventory();
    } catch (err) {
      toast.error('Failed to update stock');
    }
  };

  // Open history modal
  const openHistoryModal = async (item) => {
    setSelectedItem(item);
    setShowHistoryModal(true);
    
    try {
      const res = await api.get(`/admin/inventory/${item.item_id}/history`);
      setHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch history');
    }
  };

  // Open settings modal
  const openSettingsModal = (item) => {
    setSelectedItem(item);
    setSettingsData({
      low_stock_threshold: item.low_stock_threshold || 10,
      is_tracked: item.is_tracked !== false,
      auto_disable: item.auto_disable !== false,
      unit: item.unit || 'units'
    });
    setShowSettingsModal(true);
  };

  // Save settings
  const saveSettings = async () => {
    if (!selectedItem) return;
    
    try {
      await api.put(`/admin/inventory/${selectedItem.item_id}`, settingsData);
      toast.success('Settings saved!');
      setShowSettingsModal(false);
      fetchInventory();
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  // Get stock status
  const getStockStatus = (item) => {
    if (item.current_stock <= 0) {
      return { label: 'Out of Stock', color: 'bg-red-100 text-red-700', icon: XCircle };
    }
    if (item.current_stock <= item.low_stock_threshold) {
      return { label: 'Low Stock', color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle };
    }
    return { label: 'In Stock', color: 'bg-green-100 text-green-700', icon: CheckCircle };
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Inventory Management</h2>
          <p className="text-muted-foreground">Track and manage stock levels for your menu items</p>
        </div>
        <Button onClick={fetchInventory} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.total_items}</p>
                <p className="text-sm text-muted-foreground">Total Items</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.healthy_stock_count}</p>
                <p className="text-sm text-muted-foreground">Healthy Stock</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.low_stock_count}</p>
                <p className="text-sm text-muted-foreground">Low Stock</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.out_of_stock_count}</p>
                <p className="text-sm text-muted-foreground">Out of Stock</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card rounded-xl p-4 border border-border flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="inventory-search"
            />
          </div>
        </div>
        
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]" data-testid="inventory-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]" data-testid="inventory-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="healthy">In Stock</SelectItem>
            <SelectItem value="low">Low Stock</SelectItem>
            <SelectItem value="out">Out of Stock</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Inventory Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 font-medium text-foreground">Item</th>
                <th className="text-left p-4 font-medium text-foreground">Category</th>
                <th className="text-center p-4 font-medium text-foreground">Stock</th>
                <th className="text-center p-4 font-medium text-foreground">Status</th>
                <th className="text-center p-4 font-medium text-foreground">Available</th>
                <th className="text-right p-4 font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredInventory.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No inventory items found</p>
                  </td>
                </tr>
              ) : (
                filteredInventory.map(item => {
                  const status = getStockStatus(item);
                  const StatusIcon = status.icon;
                  const categoryName = categories.find(c => c.id === item.category)?.name || item.category;
                  
                  return (
                    <tr key={item.item_id} className="hover:bg-muted/30" data-testid={`inventory-row-${item.item_id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt={item.item_name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                              <Box className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-foreground">{item.item_name}</p>
                            <p className="text-sm text-muted-foreground">${item.price?.toFixed(2)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-muted-foreground">{categoryName}</span>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className={`text-lg font-bold ${
                            item.current_stock <= 0 ? 'text-red-600' :
                            item.current_stock <= item.low_stock_threshold ? 'text-yellow-600' :
                            'text-foreground'
                          }`}>
                            {item.current_stock}
                          </span>
                          <span className="text-xs text-muted-foreground">{item.unit || 'units'}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Min: {item.low_stock_threshold}
                        </p>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {status.label}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        {item.is_available ? (
                          <span className="text-green-600">Yes</span>
                        ) : (
                          <span className="text-red-600">No</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAdjustModal(item, true)}
                            className="gap-1"
                            data-testid={`restock-btn-${item.item_id}`}
                          >
                            <Plus className="w-3 h-3" />
                            Restock
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openAdjustModal(item, false)}
                            data-testid={`deduct-btn-${item.item_id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openHistoryModal(item)}
                            data-testid={`history-btn-${item.item_id}`}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openSettingsModal(item)}
                            data-testid={`settings-btn-${item.item_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Adjust Stock Modal */}
      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {adjustmentData.quantity >= 0 ? 'Restock Item' : 'Deduct Stock'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedItem && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                {selectedItem.image ? (
                  <img src={selectedItem.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-background flex items-center justify-center">
                    <Box className="w-6 h-6" />
                  </div>
                )}
                <div>
                  <p className="font-medium">{selectedItem.item_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Current Stock: {selectedItem.current_stock} {selectedItem.unit || 'units'}
                  </p>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Quantity</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAdjustmentData(prev => ({ ...prev, quantity: prev.quantity - 1 }))}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input
                  type="number"
                  value={adjustmentData.quantity}
                  onChange={(e) => setAdjustmentData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                  className="text-center w-24"
                  data-testid="adjustment-quantity"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAdjustmentData(prev => ({ ...prev, quantity: prev.quantity + 1 }))}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {selectedItem && (
                <p className="text-sm text-muted-foreground">
                  New stock: {Math.max(0, selectedItem.current_stock + adjustmentData.quantity)} {selectedItem.unit || 'units'}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select 
                value={adjustmentData.reason} 
                onValueChange={(v) => setAdjustmentData(prev => ({ ...prev, reason: v }))}
              >
                <SelectTrigger data-testid="adjustment-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STOCK_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={adjustmentData.notes}
                onChange={(e) => setAdjustmentData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add a note..."
                data-testid="adjustment-notes"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAdjustModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={submitAdjustment}
                className={adjustmentData.quantity >= 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="submit-adjustment"
              >
                {adjustmentData.quantity >= 0 ? (
                  <>
                    <Plus className="w-4 h-4 mr-1" />
                    Add {Math.abs(adjustmentData.quantity)}
                  </>
                ) : (
                  <>
                    <Minus className="w-4 h-4 mr-1" />
                    Remove {Math.abs(adjustmentData.quantity)}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Stock History - {selectedItem?.item_name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {history.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No history records</p>
            ) : (
              <div className="space-y-2">
                {history.map(tx => (
                  <div key={tx.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        tx.quantity_change > 0 ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {tx.quantity_change > 0 ? (
                          <TrendingUp className="w-4 h-4 text-green-600" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {tx.quantity_change > 0 ? '+' : ''}{tx.quantity_change} {selectedItem?.unit || 'units'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {tx.reason?.replace(/_/g, ' ')} {tx.notes && `- ${tx.notes}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">{tx.previous_quantity} → {tx.new_quantity}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inventory Settings - {selectedItem?.item_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Low Stock Threshold</Label>
              <Input
                type="number"
                value={settingsData.low_stock_threshold}
                onChange={(e) => setSettingsData(prev => ({ ...prev, low_stock_threshold: parseInt(e.target.value) || 0 }))}
                min={0}
                data-testid="settings-threshold"
              />
              <p className="text-xs text-muted-foreground">
                Alert when stock falls to or below this number
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Unit</Label>
              <Select 
                value={settingsData.unit} 
                onValueChange={(v) => setSettingsData(prev => ({ ...prev, unit: v }))}
              >
                <SelectTrigger data-testid="settings-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="units">Units</SelectItem>
                  <SelectItem value="cups">Cups</SelectItem>
                  <SelectItem value="portions">Portions</SelectItem>
                  <SelectItem value="servings">Servings</SelectItem>
                  <SelectItem value="kg">Kilograms</SelectItem>
                  <SelectItem value="liters">Liters</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-sm">Track Inventory</p>
                <p className="text-xs text-muted-foreground">Enable stock tracking for this item</p>
              </div>
              <Switch
                checked={settingsData.is_tracked}
                onCheckedChange={(v) => setSettingsData(prev => ({ ...prev, is_tracked: v }))}
                data-testid="settings-tracked"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-sm">Auto-disable When Out</p>
                <p className="text-xs text-muted-foreground">Automatically hide item when stock is 0</p>
              </div>
              <Switch
                checked={settingsData.auto_disable}
                onCheckedChange={(v) => setSettingsData(prev => ({ ...prev, auto_disable: v }))}
                data-testid="settings-autodisable"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowSettingsModal(false)}>
                Cancel
              </Button>
              <Button onClick={saveSettings} data-testid="save-settings">
                <Save className="w-4 h-4 mr-1" />
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
